import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
// Public page uses a minimal layout without app bar/sidebar
import ChatItem from '../../components/ChatItem';
import api from '../../utils/api';

const PublicCampaignChats = () => {
  const { id, token } = useParams();

  const [loading, setLoading] = useState(true);
  const [contactsWithChats, setContactsWithChats] = useState([]);
  const [flattenedChats, setFlattenedChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const flatten = useCallback((contactsData) => {
    const flattened = [];
    contactsData.forEach(contact => {
      Object.entries(contact.accounts).forEach(([accountPhone, accountData]) => {
        flattened.push({
          id: `${contact.contact.id}_${accountPhone}`,
          contact_id: contact.contact.id,
          account_phone: accountPhone,
          chat_id: accountData.chat_id,
          contact: contact.contact,
          account: accountData,
          unread_count: 0,
          last_message_at: accountData.last_message_at
        });
      });
    });
    return flattened.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
  }, []);

  const filteredChats = useMemo(() => {
    let result = flattenedChats;
    if (searchTerm) {
      result = result.filter(chat =>
        chat.contact?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chat.contact?.phone?.includes(searchTerm)
      );
    }
    return result;
  }, [flattenedChats, searchTerm]);

  const fetchChats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/public/campaigns/${id}/chats`, { params: { token } });
      setContactsWithChats(res.data);
      setFlattenedChats(flatten(res.data));
    } catch (e) {
      console.error('Failed to load public chats', e);
    } finally {
      setLoading(false);
    }
  }, [id, token, flatten]);

  const fetchMessages = useCallback(async (chat) => {
    if (!chat) return;
    try {
      const res = await api.get(`/public/chats/${chat.chat_id}/messages`, { params: { token } });
      setMessages(res.data.data);
    } catch (e) {
      console.error('Failed to load messages', e);
    }
  }, [token]);

  useEffect(() => { fetchChats(); }, [fetchChats]);
  useEffect(() => { if (selectedChat) fetchMessages(selectedChat); }, [selectedChat, fetchMessages]);

  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return '';
    try { return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
  }, []);

  const getStatusColor = useCallback((status) => {
    switch ((status||'').toLowerCase()) {
      case 'authorized':
      case 'connected':
        return 'success';
      case 'deleted':
        return 'error';
      default:
        return 'warning';
    }
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Grid container spacing={2} sx={{ flex: 1, m: 0, width: '100%', p: { xs: 1, sm: 2 } }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <TextField
                fullWidth
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                )}}
                size="small"
                sx={{ mb: 2 }}
              />
            </Box>

            <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 'calc(60vh - 0px)' }}>
              <List sx={{ py: 0 }}>
                {loading ? (
                  <ListItem key="loading">
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 2 }}>
                      <CircularProgress />
                    </Box>
                  </ListItem>
                ) : filteredChats.length === 0 ? (
                  <ListItem key="no-chats">
                    <ListItemText primary="No chats found" secondary="" />
                  </ListItem>
                ) : (
                  filteredChats.map((chat) => (
                    <ChatItem
                      key={chat.id}
                      chat={chat}
                      isSelected={selectedChat?.id === chat.id}
                      onSelect={setSelectedChat}
                      hasNewMessage={false}
                      formatTime={formatTime}
                      getStatusColor={getStatusColor}
                    />
                  ))
                )}
              </List>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {selectedChat ? (
              <>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">{selectedChat.contact?.name || 'Unknown Contact'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedChat.contact?.phone || 'No phone'} • {selectedChat.account_phone}
                    {selectedChat.account.active_instance && ` • ${selectedChat.account.active_instance.alias}`}
                  </Typography>
                </Box>
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column-reverse', maxHeight: 'calc(100vh - 350px)' }}>
                  {messages.length === 0 ? (
                    <Typography key="no-messages" align="center" color="text.secondary">No messages yet</Typography>
                  ) : (
                    messages.map((message, index) => (
                      <Box key={`message-${message.id || index}`} sx={{ display: 'flex', justifyContent: message.direction === 'outgoing' ? 'flex-end' : 'flex-start', mb: 2 }}>
                        <Paper sx={{ maxWidth: '70%', p: 1, bgcolor: message.direction === 'outgoing' ? 'primary.light' : 'background.paper' }}>
                          <Typography variant="body1">{message.content}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                            {formatTime(message.sent_at)}
                          </Typography>
                        </Paper>
                      </Box>
                    ))
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="h6" color="text.secondary">Select a chat to view messages</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PublicCampaignChats;
